import { fetchAffordability, fetchInterestRate } from '@hex/calculators-shared/api';
import { displayLanguageSwitcher, DevLanguageSwitcher, ResetSitecoreStyles } from '@hex/calculators-shared/components';
import { useSapphireMedia } from '@hex/calculators-shared/hooks';
import { BaseLayout, FormLayout, ResultLayout } from '@hex/calculators-shared/layout';
import { mapFormDataToAffordabilityRequest } from '@hex/calculators-shared/mappers';
import { InterestRatesResponse, ReferenceInterestRateResponse } from '@hex/calculators-shared/types';
import { AffordabilityFormDataType } from '@hex/calculators-shared/types/affordability';
import { getPartnership } from '@hex/calculators-shared/utils';
import { trackCalculatorSubmit } from '@hex/shared/tracking';
import { defaultTheme, SapphireProvider } from '@sapphire-react/core';
import { useRef, useState } from 'react';
import type { UseFormHandleSubmit, UseFormReset } from 'react-hook-form';
import { Form } from './components/Form';
import { Results } from './components/Results';
import { AffordabilityResponse } from './types';

export function App() {
  const [interestResponseData, setInterestResponseData] = useState<
    InterestRatesResponse | ReferenceInterestRateResponse | null
  >(null);
  const [affordabilityResponseData, setAffordabilityResponseData] = useState<AffordabilityResponse | null>(null);
  const [formData, setFormData] = useState<AffordabilityFormDataType>();
  const [hasError, setHasError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const { isExtraSmall, isSmall } = useSapphireMedia();
  const baseLayoutRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const submitRef = useRef<ReturnType<UseFormHandleSubmit<AffordabilityFormDataType>> | null>(null);

  const scrollIntoCalculatorTop = () => {
    if (!isExtraSmall && !isSmall) {
      const sitecoreNavbarHeight = 102;
      const top = baseLayoutRef?.current
        ? baseLayoutRef?.current?.getBoundingClientRect().top + window.scrollY - sitecoreNavbarHeight
        : undefined;

      window?.scrollTo({ top });
    }
  };

  async function onSubmit(
    data: AffordabilityFormDataType,
    isDirty: boolean,
    reset?: UseFormReset<AffordabilityFormDataType>,
  ) {
    if (!isDirty) {
      return;
    }

    setIsLoading(true);
    setHasError(false);

    try {
      const affordability = await fetchAffordability(mapFormDataToAffordabilityRequest(data));

      const interestRates = await fetchInterestRate({
        loanAmount: affordability.loanAmount.toString(),
        propertyValue: (affordability.loanAmount + parseInt(data.downpayment)).toString(),
        partnership: getPartnership(data.partnership),
      });

      setFormData(data);
      setInterestResponseData(interestRates);
      setAffordabilityResponseData(affordability);
      trackCalculatorSubmit(data.loanAmount, data.partnership);
      setIsLoading(false);

      reset?.(data, { keepValues: true });
    } catch (_e) {
      setIsLoading(false);
      setHasError(true);
    }

    scrollIntoCalculatorTop();
  }

  const handleFormChange = () => {
    setInterestResponseData(null);
    setAffordabilityResponseData(null);
  };
  const hasSmallScreen = isExtraSmall || isSmall;

  return (
    <ResetSitecoreStyles>
      <span
        style={{ display: 'none' }}
        data-build-id={import.meta.env.NX_PUBLIC_BUILD_NUMBER}
        data-git-hash={import.meta.env.NX_PUBLIC_GIT_HASH}
      />
      <SapphireProvider theme={defaultTheme} locale={'sv-SE'} width={'100%'} overlayZIndex={1000}>
        {displayLanguageSwitcher && <DevLanguageSwitcher languages={['se']} />}
        <BaseLayout ref={baseLayoutRef}>
          <FormLayout showRightBorderRadius={hasSmallScreen}>
            <Form
              onSubmit={onSubmit}
              buttonRef={buttonRef}
              submitRef={submitRef}
              interestResponseData={interestResponseData}
              affordabilityResponseData={affordabilityResponseData}
              onFormChange={handleFormChange}
              loading={isLoading}
            />
          </FormLayout>
          <ResultLayout
            showLeftBorderRadius={hasSmallScreen}
            focus={hasSmallScreen && !!interestResponseData && !!affordabilityResponseData}
            isHidden={hasSmallScreen && !interestResponseData && !affordabilityResponseData && !isLoading && !hasError}
          >
            <Results
              interestResponseData={interestResponseData}
              affordabilityResponseData={affordabilityResponseData}
              hasError={hasError}
              isLoading={isLoading}
              formData={formData}
              submitRef={submitRef}
              onError={() => setHasError(true)}
            />
          </ResultLayout>
        </BaseLayout>
      </SapphireProvider>
    </ResetSitecoreStyles>
  );
}
